import { useMemo, useState } from "react";
import InfoTip from "@/shared/components/infoTip/InfoTip";
import TouchRow, {
  type TouchRowModel,
} from "src/modules/engagement/components/phases/emailSms/TouchRow";
import {
  anchorName,
  markPairs,
  type Sequence,
  type Touch,
} from "src/modules/engagement/components/phases/emailSms/emailSmsTypes";
import FilterMenu from "./FilterMenu";
import { PRACTICES } from "./catalogData";

type ChannelFilter = "all" | "email" | "sms";
type StatusFilter = "all" | "published" | "draft";

const STATUS_OPTIONS = [
  { value: "published", label: "Published" },
  { value: "draft", label: "Draft" },
];

const USE_OPTIONS = [
  { value: "used", label: "Used by a campaign" },
  { value: "unused", label: "Not used yet" },
];

type Props = {
  selectedId: string | null;
  onSelect: (sequenceId: string) => void;
  onOpenTouch: (touchId: string) => void;
  sequences: Sequence[];
  /** Campaigns with each playbook switched on, counted server-side. */
  usedIn: Record<string, number>;
  /** The stages a playbook can be filed under, from the Sequence Stage tag group. */
  stageOptions: string[];
  onSetPublished: (sequenceId: number, published: boolean) => void;
  isBusy?: boolean;
};

export function SequencesTab({
  selectedId,
  onSelect,
  onOpenTouch,
  sequences: SEQUENCES,
  usedIn: USED_IN,
  stageOptions: STAGES,
  onSetPublished,
  isBusy = false,
}: Props) {
  const [query, setQuery] = useState("");
  const [stages, setStages] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [uses, setUses] = useState<string[]>([]);
  const [channel, setChannel] = useState<ChannelFilter>("all");
  const [touchStatus, setTouchStatus] = useState<StatusFilter>("all");
  const [closed, setClosed] = useState<Record<string, boolean>>({});

  const playbooks = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return SEQUENCES.filter((sequence) => {
      if (stages.length && !stages.includes(sequence.stage)) return false;
      if (statuses.length) {
        const status = sequence.published ? "published" : "draft";
        if (!statuses.includes(status)) return false;
      }
      if (uses.length) {
        const used = (USED_IN[sequence.id] ?? 0) > 0 ? "used" : "unused";
        if (!uses.includes(used)) return false;
      }
      if (!needle) return true;
      return `${sequence.name} ${sequence.target}`.toLowerCase().includes(needle);
    });
  }, [query, stages, statuses, uses]);

  const selected = playbooks.find((s) => s.id === selectedId) ?? playbooks[0] ?? null;

  // Grouped by funnel stage, in journey order rather than alphabetically.
  const groups = useMemo(
    () =>
      STAGES.map((stage) => ({
        name: stage,
        items: playbooks.filter((s) => s.stage === stage),
      })).filter((g) => g.items.length > 0),
    [playbooks],
  );

  const anyFilter =
    query !== "" || stages.length > 0 || statuses.length > 0 || uses.length > 0;

  const visibleTouches = (selected?.touches ?? []).filter((touch) => {
    if (channel !== "all" && touch.ch !== channel) return false;
    if (touchStatus === "published" && !touch.pub) return false;
    if (touchStatus === "draft" && touch.pub) return false;
    return true;
  });

  const pairs = markPairs(visibleTouches);
  const anchorOptions = selected
    ? Array.from(new Set(selected.touches.map((t) => t.anchor))).map((code) => ({
        v: code,
        label: anchorName(code),
      }))
    : [];

  const toModel = (touch: Touch, index: number): TouchRowModel => ({
    ...touch,
    n: index + 1,
    pair: pairs.get(touch.id),
    hasDays: touch.dir !== "on",
    hasDrag: true,
    hasStop: touch.ch === "sms",
    anchorOptions,
    statusLabel: touch.pub ? "Published" : "Draft",
    statusTone: touch.pub ? "on" : "blocked",
  });

  const sections = selected
    ? (selected.groups.length ? selected.groups : [{ id: "main", name: "", note: "" }]).map(
        (group) => ({
          ...group,
          label: group.name || "All touches",
          rows: visibleTouches.filter((t) => (t.grp ?? "main") === group.id),
        }),
      )
    : [];

  return (
    <>
      <div className="cat-filters">
        <input
          type="search"
          className="cat-search"
          value={query}
          placeholder="Search playbooks or target actions"
          aria-label="Search playbooks"
          onChange={(e) => setQuery(e.target.value)}
        />
        <FilterMenu
          allLabel="All stages"
          options={STAGES.map((s) => ({ value: s, label: s }))}
          selected={stages}
          onChange={setStages}
        />
        <FilterMenu
          allLabel="All statuses"
          options={STATUS_OPTIONS}
          selected={statuses}
          onChange={setStatuses}
        />
        <FilterMenu
          allLabel="All usage"
          options={USE_OPTIONS}
          selected={uses}
          onChange={setUses}
        />
        {anyFilter && (
          <button
            type="button"
            className="esms__link"
            onClick={() => {
              setQuery("");
              setStages([]);
              setStatuses([]);
              setUses([]);
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="cat-split">
        <div className="cat-playbooks">
          <div className="cat-table__head">
            <span className="cat-table__title">Playbooks</span>
            <span className="esms-count">{playbooks.length}</span>
            <InfoTip
              label="Playbooks"
              text="One playbook per funnel stage. Variants are coming later."
            />
          </div>
          {groups.length === 0 ? (
            <div className="cat-empty">
              <div className="cat-empty__title">No playbooks match</div>
              <div className="cat-empty__body">Try another search or clear filters.</div>
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.name}>
                <div className="cat-playbooks__group">{group.name}</div>
                {group.items.map((sequence) => (
                  <button
                    type="button"
                    key={sequence.id}
                    aria-current={selected?.id === sequence.id}
                    className={`cat-playbook${selected?.id === sequence.id ? " cat-playbook--on" : ""}`}
                    onClick={() => onSelect(sequence.id)}
                  >
                    <span className="cat-playbook__top">
                      <span className="cat-playbook__name">{sequence.name}</span>
                      {!sequence.published && (
                        <span className="cat-playbook__dot" title="Draft" aria-label="Draft" />
                      )}
                    </span>
                    <span className="cat-playbook__bottom">
                      <span className="esms-count">{sequence.stage}</span>
                      <span className="cat-playbook__count">
                        {sequence.touches.length} touches
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            ))
          )}
        </div>

        {selected && (
          <div className="cat-seq">
            <div className="cat-seq__card">
              <div className="cat-seq__title-row">
                <span className="cat-seq__title">{selected.name}</span>
                <span className={`touch-pill touch-pill--${selected.published ? "on" : "blocked"}`}>
                  {selected.published ? "Published" : "Draft"}
                </span>
                <span className="esms__spacer" />
                {/* Reversible both ways (MPN-2201). A playbook published by mistake used to
                    be stuck that way; campaigns already running it are left alone either
                    way, because those carry client approval against copy the client read. */}
                <button
                  type="button"
                  className={`btn btn-sm ${selected.published ? "btn-outline-secondary" : "btn-primary"}`}
                  disabled={isBusy}
                  onClick={() => onSetPublished(Number(selected.id), !selected.published)}
                >
                  {selected.published ? "Unpublish" : "Publish sequence"}
                </button>
              </div>

              <div className="cat-seq__facts">
                <span className="cat-seq__key">Target action</span>
                <span className="cat-seq__val cat-seq__val--strong">{selected.target}</span>
                <span className="cat-seq__key">Window</span>
                <span className="cat-seq__val">{selected.win}</span>
                <span className="cat-seq__key">Used in</span>
                <span className="cat-seq__val">
                  {USED_IN[selected.id] ?? 0} campaign
                  {(USED_IN[selected.id] ?? 0) === 1 ? "" : "s"}
                </span>
              </div>

              <div className="cat-seq__starter">
                <span className="cat-seq__swatch">
                  <span />
                  <span />
                  <span />
                </span>
                <span className="cat-seq__starter-text">
                  <span>Starting design:</span> <strong>{selected.starter}</strong>
                </span>
                <button type="button" className="esms__link" disabled>
                  Change
                </button>
                <span className="esms__spacer" />
                <span className="cat-seq__practices">
                  Email practices
                  <InfoTip label="Email practices" text={PRACTICES} align="right" />
                </span>
              </div>
            </div>

            <div className="cat-seq__touches">
              <div className="cat-table__head">
                <span className="cat-table__title">Touches</span>
                <InfoTip
                  label="Touches"
                  text="Each message sends a set number of days before, after or on a key date. These are the defaults a campaign gets when it turns the sequence on, and each campaign can change them."
                />
                <span className="esms-count">{visibleTouches.length}</span>
                <span className="esms__spacer" />
                <div className="btn-group btn-group-sm" role="group" aria-label="Channel">
                  {(["all", "email", "sms"] as ChannelFilter[]).map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={`btn btn-sm ${channel === option ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => setChannel(option)}
                    >
                      {option === "all" ? "All" : option === "email" ? "Email" : "SMS"}
                    </button>
                  ))}
                </div>
                <div className="btn-group btn-group-sm" role="group" aria-label="Touch status">
                  {(["all", "published", "draft"] as StatusFilter[]).map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={`btn btn-sm ${touchStatus === option ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => setTouchStatus(option)}
                    >
                      {option === "all" ? "All" : option === "published" ? "Published" : "Draft"}
                    </button>
                  ))}
                </div>
              </div>

              {visibleTouches.length === 0 ? (
                <div className="cat-empty">
                  <div className="cat-empty__body">No touches match.</div>
                  <button
                    type="button"
                    className="esms__link"
                    onClick={() => {
                      setChannel("all");
                      setTouchStatus("all");
                    }}
                  >
                    Clear filters
                  </button>
                </div>
              ) : (
                <>
                  <TouchRow header />
                  {sections.map((section) => {
                    const open = !closed[section.id];
                    if (section.rows.length === 0) return null;
                    return (
                      <div key={section.id}>
                        <button
                          type="button"
                          className="cat-section"
                          aria-expanded={open}
                          onClick={() =>
                            setClosed((prev) => ({ ...prev, [section.id]: open }))
                          }
                        >
                          <i className={open ? "bi bi-chevron-down" : "bi bi-chevron-right"} />
                          <span className="cat-section__label">{section.label}</span>
                          <span className="cat-section__count">· {section.rows.length}</span>
                        </button>
                        {open && (
                          <>
                            {section.rows.map((touch, index) => (
                              <TouchRow
                                key={touch.id}
                                touch={toModel(touch, index)}
                                onPreview={() => onOpenTouch(touch.id)}
                              />
                            ))}
                            <div className="cat-add">
                              <button type="button" className="btn btn-link btn-sm" disabled>
                                + Add touch
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default SequencesTab;
